import { memo, useRef, useEffect } from 'react'
import { InputAdornment, MenuItem, Select, TextField, Tooltip, Typography } from '@mui/material'
import { NumericFormat } from 'react-number-format'
import { getFieldId } from '../utils/utils'
import { useAllValues, useFieldCurrencyMap, useProcessContext } from '../../../store'
import { ACTIVITY_TYPES, CURRENCY_COUNTRY_CODE } from '../../../utility/constants'
import { currencyFieldService } from '../../../services/currencyFieldService'
import { useAuthContext } from '../../../hooks/useAuthContext'
import { computeCurrency } from '../utils/currency'
import FieldHistory from '../../../components/DataEntry/FieldHistory'

const CurrencyField = ({
  id,
  value,
  label = null,
  placeholder = null,
  className,
  disabled,
  helperText = null,
  style,
  autoFocus = false,
  onChange = () => {},
  field,
  processId,
  inputSx = {},
}) => {
  const inputRef = useRef(null)
  const { user } = useAuthContext()
  const processContext = useProcessContext((state) => state.processContext)
  const activityType = processContext?.activityTemplate?.activity_type
  const defaultCurrency = processContext?.companyDetails?.default_currency_code
  const fieldCurrencyMap = useFieldCurrencyMap((state) => state.fieldCurrencyMap)
  const setFieldCurrencyMap = useFieldCurrencyMap((state) => state.setFieldCurrencyMap)
  const allValues = useAllValues((state) => state.allValues)
  const allFields = processContext.allFields

  useEffect(() => {
    if (autoFocus) {
      inputRef.current?.focus()
    }
  }, [autoFocus])

  const deleteNonDefaultCurrency = async (id) => {
    try {
      await currencyFieldService.deleteNonDefaultCurrency(user, processId, id)
    } catch (error) {
      console.error('Error deleting non-default currency:', error)
    }
  }

  const saveNonDefaultCurrencyToDb = async (newValueObj) => {
    const currencyMap = newValueObj
      ? { ...fieldCurrencyMap, [newValueObj?.id]: newValueObj?.currency }
      : fieldCurrencyMap

    const { updatedFieldCurrencyMap, nonDefaultValues } = computeCurrency(
      currencyMap,
      allFields,
      defaultCurrency,
      allValues
    )

    const map = {
      ...updatedFieldCurrencyMap,
      ...nonDefaultValues.reduce((acc, cur) => {
        return { ...acc, [cur.id]: cur.currency }
      }, {}),
    }

    setFieldCurrencyMap(map)

    if (newValueObj) {
      await currencyFieldService.addNonDefaultCurrency(user, {
        field_template_id: id,
        currency_code: Object.keys(CURRENCY_COUNTRY_CODE).find(
          (key) => CURRENCY_COUNTRY_CODE[key] === newValueObj.currency
        ),
        process_id: processContext?.processData?.id,
      })
    }

    for (const { id, currency } of nonDefaultValues) {
      if (CURRENCY_COUNTRY_CODE[defaultCurrency] === currency) {
        await deleteNonDefaultCurrency(id)
      } else {
        const currencies = Object.entries(CURRENCY_COUNTRY_CODE).find(
          ([_, value]) => value === currency
        )

        if (currency) {
          await currencyFieldService.addNonDefaultCurrency(user, {
            field_template_id: id,
            currency_code: currencies[0],
            process_id: processContext?.processData?.id,
          })
        }
      }
    }
  }

  const getSelectedCurrency = (fieldId) => {
    const currency = fieldCurrencyMap[fieldId]
      ? Object.entries(CURRENCY_COUNTRY_CODE).find(
          ([key, value]) => value === fieldCurrencyMap[fieldId]
        )?.[0]
      : defaultCurrency

    return currency
  }

  const currencyInput = (
    <NumericFormat
      id={getFieldId(id)}
      key={getFieldId(id)}
      customInput={TextField}
      autoFocus={autoFocus}
      thousandSeparator=","
      decimalScale={2}
      thousandsGroupStyle="lakh"
      onValueChange={onChange}
      style={style}
      label={label}
      placeholder={placeholder}
      name={getFieldId(id)}
      value={fieldCurrencyMap[id] === '?' ? 0 : value}
      fullWidth
      className={className}
      size="small"
      variant="outlined"
      color="primary"
      error={helperText != null}
      sx={inputSx}
      InputProps={{
        startAdornment: (
          <Tooltip
            title={
              fieldCurrencyMap[id] === '?' ? 'Cannot compute due to disparate currencies.' : null
            }
          >
            <InputAdornment position="start" style={{ fontSize: '13px' }}>
              <Typography fontSize={'13px'}>
                {fieldCurrencyMap[id] || CURRENCY_COUNTRY_CODE[defaultCurrency]}
              </Typography>
            </InputAdornment>
          </Tooltip>
        ),
        endAdornment:
          field?.is_subfield === false &&
          !field.is_computed &&
          !field.is_derived_field &&
          !field.is_read_only &&
          activityType != ACTIVITY_TYPES.REVIEW ? (
            <Select
              value={getSelectedCurrency(field.id) || defaultCurrency}
              onChange={async (e) =>
                await saveNonDefaultCurrencyToDb({
                  id: field.id,
                  currency: CURRENCY_COUNTRY_CODE[e.target.value],
                })
              }
              variant="standard"
              size="small"
              disabled={disabled}
              style={{ marginLeft: '8px' }}
            >
              {Object.keys(CURRENCY_COUNTRY_CODE).map((currency) => (
                <MenuItem key={currency} value={currency}>
                  {CURRENCY_COUNTRY_CODE[currency]}
                </MenuItem>
              ))}
            </Select>
          ) : null,
        disabled: disabled,
        sx: inputSx,
      }}
    />
  )

  if (!field || field.is_subfield) {
    return <div>{currencyInput}</div>
  } else {
    return (
      <FieldHistory
        fieldId={field?.id}
        field={field}
        dynamicListData={field?.dynamicListData}
        processId={processId}
        allValues={allValues}
        helperText={helperText}
      >
        {currencyInput}
      </FieldHistory>
    )
  }
}

export default memo(
  CurrencyField,
  (prevProps, nextProps) =>
    prevProps.value === nextProps.value && prevProps.helperText === nextProps.helperText
)
